import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { paymentsAPI, authAPI } from '@/lib/api';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { User, Lock, CreditCard, Trash2, Check, AlertTriangle, Loader2, Calendar } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import useSEO from '@/hooks/useSEO';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'YOUR_PAYPAL_CLIENT_ID_HERE';

const Settings = () => {
  useSEO({ title: 'Settings — EpisodeKit', path: '/settings', index: false });
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [processingPlan, setProcessingPlan] = useState(null);

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setPasswordLoading(true);
    try {
      await authAPI.updatePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password updated successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setDeleteLoading(true);
    try {
      await authAPI.deleteAccount();
      toast.success('Account deleted successfully');
      logout();
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setDeleteLoading(false);
    }
  };

  const createPayPalOrder = async (planId) => {
    try {
      const response = await paymentsAPI.createPayPalOrder(planId);
      return response.data.order_id;
    } catch (error) {
      toast.error('Failed to create order. Please try again.');
      throw error;
    }
  };

  const onPayPalApprove = async (data, planId) => {
    try {
      setProcessingPlan(planId);
      const response = await paymentsAPI.capturePayPalOrder(data.orderID);
      
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        // Refresh user data
        const userResponse = await authAPI.getMe();
        updateUser(userResponse.data);
      }
    } catch (error) {
      toast.error('Payment failed. Please try again.');
    } finally {
      setProcessingPlan(null);
    }
  };

  const plans = [
    { 
      id: 'free', 
      name: 'Free', 
      price: 0, 
      currency: '€',
      episodes: 1, 
      features: [
        '1 episode per month',
        'English only',
        'AI transcription',
        'Show notes generation',
        'Blog post generation',
        'Social snippets',
        'Watermarked exports'
      ] 
    },
    { 
      id: 'basic', 
      name: 'Basic', 
      price: 12, 
      currency: '€',
      episodes: 5, 
      features: [
        '5 episodes per month',
        'English only',
        'AI transcription',
        'Show notes generation',
        'Blog post generation',
        'Social snippets',
        'No watermark',
        'Email support'
      ] 
    },
    { 
      id: 'pro', 
      name: 'Pro', 
      price: 29, 
      currency: '€',
      episodes: 15, 
      popular: true,
      features: [
        '15 episodes per month',
        '5 tone presets',
        'AI transcription',
        'Show notes generation',
        'Blog post generation',
        'Social snippets',
        'No watermark',
        'Priority support',
        'Priority processing'
      ] 
    },
    { 
      id: 'unlimited', 
      name: 'Unlimited', 
      price: 59, 
      currency: '€',
      episodes: 'Unlimited', 
      features: [
        'Unlimited episodes',
        '5 tone presets',
        'AI transcription',
        'Show notes generation',
        'Blog post generation',
        'Social snippets',
        'No watermark',
        'Priority processing',
        'Premium support'
      ] 
    }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <PayPalScriptProvider options={{ 
      clientId: PAYPAL_CLIENT_ID,
      currency: 'USD',
      intent: 'capture'
    }}>
      <div className="flex min-h-screen bg-[#050505]" data-testid="settings-page">
        <Sidebar />
        
        <main className="flex-1 p-8">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl font-unbounded font-bold mb-8">Settings</h1>

            {/* Profile Section */}
            <motion.section 
              className="card-base mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Profile</h2>
                  <p className="text-neutral-400 text-sm">Your account information</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-neutral-400 text-sm">Name</Label>
                  <p className="font-medium">{user?.name}</p>
                </div>
                <div>
                  <Label className="text-neutral-400 text-sm">Email</Label>
                  <p className="font-medium">{user?.email}</p>
                </div>
                <div>
                  <Label className="text-neutral-400 text-sm">Member Since</Label>
                  <p className="font-medium">{formatDate(user?.created_at)}</p>
                </div>
                <div>
                  <Label className="text-neutral-400 text-sm">Current Plan</Label>
                  <p className="font-medium capitalize text-primary">{user?.plan}</p>
                </div>
              </div>
            </motion.section>

            {/* Subscription Status */}
            {user?.subscription_status && user?.plan !== 'free' && (
              <motion.section 
                className="card-base mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-success" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Subscription Status</h2>
                    <p className="text-neutral-400 text-sm">Your current subscription details</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-neutral-400 text-sm">Status</Label>
                    <p className={`font-medium capitalize ${
                      user?.subscription_status === 'active' ? 'text-success' : 
                      user?.subscription_status === 'cancelled' && user?.is_subscription_active ? 'text-amber-400' : 
                      'text-warning'
                    }`}>
                      {user?.subscription_status === 'cancelled' && user?.is_subscription_active
                        ? 'Active until period end'
                        : user?.subscription_status}
                    </p>
                  </div>
                  <div>
                    <Label className="text-neutral-400 text-sm">
                      {user?.subscription_status === 'cancelled' && user?.is_subscription_active
                        ? 'Access Until'
                        : 'Renewal Date'}
                    </Label>
                    <p className="font-medium">{formatDate(user?.subscription_end_date)}</p>
                  </div>
                </div>
              </motion.section>
            )}

            {/* Current Plan */}
            <motion.section 
              className="card-base mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Subscription Plans</h2>
                  <p className="text-neutral-400 text-sm">Upgrade your plan with PayPal</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {plans.map((plan) => {
                  const isCurrent = user?.plan === plan.id;
                  const isProcessing = processingPlan === plan.id;
                  return (
                    <div 
                      key={plan.id}
                      className={`p-4 rounded-lg border relative ${plan.popular ? 'border-primary bg-primary/5' : isCurrent ? 'border-green-500 bg-green-500/5' : 'border-neutral-800'}`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span className="bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
                            Most Popular
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold">{plan.name}</h3>
                        {isCurrent && (
                          <span className="badge badge-completed">Current</span>
                        )}
                      </div>
                      <p className="text-2xl font-unbounded font-bold mb-3">
                        {plan.currency || '€'}{plan.price}<span className="text-sm text-neutral-400 font-normal">/mo</span>
                      </p>
                      <ul className="space-y-1 text-sm text-neutral-400 mb-4">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <Check className="w-3 h-3 text-success" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      {!isCurrent && plan.id !== 'free' && (
                        isProcessing ? (
                          <div className="flex items-center justify-center py-2">
                            <div className="spinner w-5 h-5" />
                            <span className="ml-2 text-sm">Processing...</span>
                          </div>
                        ) : (
                          <div className="paypal-button-container-small">
                            <PayPalButtons
                              style={{ 
                                layout: 'horizontal',
                                color: 'blue',
                                shape: 'pill',
                                label: 'pay',
                                height: 35
                              }}
                              createOrder={() => createPayPalOrder(plan.id)}
                              onApprove={(data) => onPayPalApprove(data, plan.id)}
                              onError={() => toast.error('PayPal error occurred')}
                              onCancel={() => toast.info('Payment cancelled')}
                            />
                          </div>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.section>

            {/* Password Section */}
            <motion.section 
              className="card-base mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-warning/20 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-warning" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Security</h2>
                  <p className="text-neutral-400 text-sm">Update your password</p>
                </div>
              </div>

              <form onSubmit={handlePasswordUpdate} className="space-y-4">
                <div>
                  <Label htmlFor="current_password">Current Password</Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    className="input-base w-full mt-1"
                    data-testid="current-password-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                      className="input-base w-full mt-1"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm_password">Confirm Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                      className="input-base w-full mt-1"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="btn-secondary"
                  disabled={passwordLoading}
                  data-testid="update-password-btn"
                >
                  {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Password'}
                </Button>
              </form>
            </motion.section>

            {/* Danger Zone */}
            <motion.section 
              className="card-base border-red-500/30"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-red-500">Danger Zone</h2>
                  <p className="text-neutral-400 text-sm">Permanently delete your account</p>
                </div>
              </div>

              <p className="text-neutral-400 text-sm mb-4">
                Once you delete your account, there is no going back. All your episodes, content, and data will be permanently removed.
              </p>

              <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogTrigger asChild>
                  <Button 
                    variant="destructive" 
                    className="bg-red-500/10 text-red-500 hover:bg-red-500/20 border border-red-500/30"
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Account
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0A0A] border-neutral-800">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-500">
                      <AlertTriangle className="w-5 h-5" />
                      Delete Account
                    </DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <p className="text-neutral-400 mb-4">
                      This action cannot be undone. All your data will be permanently deleted.
                    </p>
                    <Label>Type DELETE to confirm</Label>
                    <Input
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      placeholder="DELETE"
                      className="input-base w-full mt-2"
                      data-testid="delete-confirm-input"
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setShowDeleteDialog(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      className="bg-red-500 hover:bg-red-600"
                      onClick={handleDeleteAccount}
                      disabled={deleteLoading || deleteConfirm !== 'DELETE'}
                      data-testid="confirm-delete-btn"
                    >
                      {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete Account'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </motion.section>
          </div>
        </main>
      </div>
    </PayPalScriptProvider>
  );
};

export default Settings;
